import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, ShieldCheck, Loader2, Users } from 'lucide-react';

/**
 * Passcode-gated control to flip the Family Mode feature flag in production.
 * Used both on the /admin page and (revealed) inside Account settings.
 */
export const FamilyModeAdmin = () => {
  const familyMode = useApp((s) => s.familyMode);
  const setConfig = useApp((s) => s.setConfig);
  const [passcode, setPasscode] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState(false);

  const unlock = async () => {
    if (!passcode.trim()) return;
    setBusy(true);
    try {
      const res = await api.adminVerify(passcode.trim());
      setUnlocked(true);
      setConfig({ family_mode_enabled: !!res?.settings?.family_mode_enabled });
      toast.success('Admin unlocked');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = err?.response?.status;
      toast.error(detail || (code === 401 ? 'Sign in as the admin account first' : 'Invalid passcode'));
    } finally { setBusy(false); }
  };

  const toggle = async (value) => {
    setBusy(true);
    try {
      const res = await api.adminSetSettings(passcode.trim(), value);
      setConfig({ family_mode_enabled: !!res?.family_mode_enabled });
      toast.success(value ? 'Family Mode is now ON for everyone' : 'Family Mode is now OFF for everyone');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update. Check your passcode.');
    } finally { setBusy(false); }
  };

  if (!unlocked) {
    return (
      <div className="rounded-xl p-3 border" style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)' }} data-testid="admin-passcode-panel">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={15} style={{ color: 'var(--text-muted)' }} />
          <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>Admin controls</span>
        </div>
        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Enter the admin passcode to manage feature flags.</p>
        <div className="flex gap-2">
          <Input
            type="password"
            inputMode="numeric"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') unlock(); }}
            placeholder="Passcode"
            data-testid="admin-passcode-input"
            aria-label="Admin passcode"
          />
          <Button onClick={unlock} disabled={busy || !passcode.trim()} data-testid="admin-unlock-button">
            {busy ? <Loader2 size={16} className="animate-spin" /> : 'Unlock'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-3 border" style={{ background: 'var(--surface-alt)', borderColor: 'var(--primary)' }} data-testid="admin-flags-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} style={{ color: 'var(--primary)' }} />
        <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>Feature flags</span>
      </div>
      <div className="flex items-center justify-between rounded-lg p-3 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2.5 pr-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: 'color-mix(in srgb, var(--primary) 14%, transparent)' }}>
            <Users size={18} style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <div className="text-sm font-bold" style={{ color: 'var(--text)' }}>Family Mode</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {familyMode ? 'Visible to all users' : 'Hidden from all users'}
            </div>
          </div>
        </div>
        <Switch
          checked={familyMode}
          disabled={busy}
          onCheckedChange={toggle}
          data-testid="family-mode-switch"
          aria-label="Toggle Family Mode"
        />
      </div>
      <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
        Changes take effect immediately for everyone. Turn on only once payments are live.
      </p>
    </div>
  );
};
